import { queryOptions } from '@tanstack/react-query'
import type { ApiLatencyBucket, ApiLatencySurface } from '@headline/schemas'
import { api } from './api'
import { fail } from './query-fail'

/**
 * The public API's latency (`/api/api-latency`, Clipbook staff — CLI-1041). The three reads come from
 * clipbook-api and are null where it is not connected (503), so the page says so rather than showing zeros;
 * any other failure throws the API's own words, a refusal included. Keys carry hour-rounded windows
 * (`latencyWindow`) and the filters' few values, never anything typed.
 */

export interface LatencyFilters {
  from: string
  to: string
  surface?: ApiLatencySurface | undefined
  operation?: string | undefined
  endpointId?: string | undefined
}

/** A filter left unset is left out of the request, not sent empty. */
function queryOf(filters: LatencyFilters) {
  return {
    from: filters.from,
    to: filters.to,
    ...(filters.surface ? { surface: filters.surface } : {}),
    ...(filters.operation ? { operation: filters.operation } : {}),
    ...(filters.endpointId ? { endpointId: filters.endpointId } : {}),
  }
}

const keyOf = (filters: LatencyFilters) => [
  filters.from,
  filters.to,
  filters.surface ?? null,
  filters.operation ?? null,
  filters.endpointId ?? null,
]

/** Latency figures change by the minute at most; a re-read sooner tells nobody anything. */
const STALE_MS = 60_000

/**
 * The endpoints the filter offers, from Headline's own table. Also what the page's loader waits for: it is
 * behind the same gate as the reads, so a refused visitor gets the gate's sentence before anything else.
 */
export const latencyEndpointsQuery = queryOptions({
  queryKey: ['api-latency', 'endpoints'],
  queryFn: async () => {
    const res = await api.api['api-latency'].endpoints.$get()
    if (!res.ok) return fail(res, 'Failed to load the API’s endpoints')
    return res.json()
  },
  staleTime: 5 * 60_000,
})

/** Each operation over the range against the seven days before it. */
export const latencySummaryQuery = (filters: LatencyFilters) =>
  queryOptions({
    queryKey: ['api-latency', 'summary', ...keyOf(filters)],
    queryFn: async () => {
      const res = await api.api['api-latency'].summary.$get({ query: queryOf(filters) })
      if (res.status === 503) return null
      if (!res.ok) return fail(res, 'Failed to load API latency')
      return res.json()
    },
    staleTime: STALE_MS,
  })

/** One operation's percentiles per hour or day. */
export const latencyTrendQuery = (filters: LatencyFilters, bucket: ApiLatencyBucket) =>
  queryOptions({
    queryKey: ['api-latency', 'trend', ...keyOf(filters), bucket],
    queryFn: async () => {
      const res = await api.api['api-latency'].trend.$get({
        query: { ...queryOf(filters), bucket },
      })
      if (res.status === 503) return null
      if (!res.ok) return fail(res, 'Failed to load the latency trend')
      return res.json()
    },
    staleTime: STALE_MS,
  })

/** Per serving revision and operation. */
export const latencyRevisionsQuery = (filters: LatencyFilters) =>
  queryOptions({
    queryKey: ['api-latency', 'revisions', ...keyOf(filters)],
    queryFn: async () => {
      const res = await api.api['api-latency'].revisions.$get({ query: queryOf(filters) })
      if (res.status === 503) return null
      if (!res.ok) return fail(res, 'Failed to load latency by revision')
      return res.json()
    },
    staleTime: STALE_MS,
  })
